import {
  BlockTypeSelect,
  BoldItalicUnderlineToggles,
  headingsPlugin,
  listsPlugin,
  ListsToggle,
  markdownShortcutPlugin,
  MDXEditor,
  MDXEditorMethods,
  quotePlugin,
  toolbarPlugin,
  UndoRedo,
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { UAParser } from 'ua-parser-js';
import editorInfo from '../../../editorInfo.json';
import { sendFeedbackMessage } from '../../../functions/API';
import { SessionData, useSessionStore } from '../../../hooks/stores/sessionStore';
import { UIData, useUIStore } from '../../../hooks/stores/uiStore';
import { Dialog } from '../../common/Dialog';
import { DropdownInput } from '../../common/baseInputs/DropdownInput';
import { useScene } from '../../../context/sceneContext';

export const FeedbackModal = () => {
  const scene = useScene();
  const [UIData] = useUIStore<UIData>();
  const [sessionData] = useSessionStore<SessionData>();
  const ref = useRef<MDXEditorMethods>(null);


  const [issueType, setIssueType] = useState('bug');
  const isOpen = UIData.openModalIDs.includes('feedbackModal');
  const { email, planName, paymentModel, themeID, versionID } = sessionData;

  const domain = 'https://editor.patchkit.net/';

  const header =
    issueType === 'bug' ? `# [ ${issueType} ]` : `## [ ${issueType} ]`;
  const userInfo = `**${email}** | ${planName} | ${paymentModel}`;
  const themeInfo = `[\`${themeID}\`](${domain}?themeId=${themeID}) | [\`${versionID}\`](${domain}?themeId=${themeID}&versionId=${versionID})`;
  const parser = new UAParser(navigator.userAgent);
  const parserResults = parser.getResult();

  const [isWindows11, setIsWindows11] = useState(false);

  useEffect(() => {
    const detectWindows11 = async () => {
      try {
        if ('userAgentData' in navigator) {
          const highEntropyValues = await (
            navigator as any
          ).userAgentData?.getHighEntropyValues(['platformVersion']);
          if (highEntropyValues?.platformVersion >= '13') {
            setIsWindows11(true);
            return;
          }
        }

        const buildNumber = parseInt(
          parserResults.os.version?.split('.')[2] ?? '0'
        );
        setIsWindows11(
          parserResults.os.name === 'Windows' && buildNumber >= 22000
        );
      } catch {
        setIsWindows11(parserResults.os.version?.startsWith('11') ?? false);
      }
    };

    detectWindows11();
  }, []);

  const systemInfo = isWindows11
    ? 'Windows 11'
    : `${parserResults.os.name ?? 'Unknown'} ${parserResults.os.version ?? ''}`;
  const browserInfo = `${parserResults.browser.name ?? 'Unknown'} ${parserResults.browser.version ?? ''}`;
  const editorVersionInfo = `${editorInfo.environment === 'staging' ? 'STAGING' : ''} v${editorInfo.versionNumber} ${editorInfo.versionLabel}`;

  const preMessage = `${header}\n${editorVersionInfo}\n${userInfo}\n${themeInfo}\n${systemInfo} | ${browserInfo}`;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={() => scene.closeModal('feedbackModal')}
      label="Help us improve"
      options={[
        {
          text: 'Send',
          onClick: () => {
            const markdown = ref.current?.getMarkdown() ?? '';
            const message = preMessage + '\n\n' + markdown;
            if (markdown.length < 50) {
              toast.error(
                <>
                  <span>Please enter a message</span>
                  <br />
                  <span>({'>'} 50 characters)</span>
                </>
              );
              return;
            }
            toast.promise(sendFeedbackMessage({ message }), {
              pending: 'Sending Feedback Message',
              success: 'Feedback has been sent. Thank you!',
              error: 'Error while sending the feedback',
            });
            scene.closeModal('feedbackModal');
          },
          type: 'primary',
        },
      ]}
    >
      <TextEditorContainer onKeyDown={(e) => e.stopPropagation()}>
        <MDXEditor
          ref={ref}
          className="mdxeditor-dark"
          markdown=""
          plugins={[
            headingsPlugin(),
            listsPlugin(),
            quotePlugin(),
            markdownShortcutPlugin(),
            toolbarPlugin({
              toolbarContents: () => (
                <>
                  <UndoRedo />
                  <BlockTypeSelect />
                  <BoldItalicUnderlineToggles />
                  <ListsToggle />
                </>
              ),
            }),
          ]}
        />
      </TextEditorContainer>
      <DropdownInput
        value={issueType}
        onChange={(value) => setIssueType(String(value))}
        options={[
          { value: 'bug', label: 'Bug Report' },
          { value: 'feedback', label: 'Feedback' },
        ]}
        canBeCleared={false}
        isSearchable={false}
        style={{ margin: '10px 0' }}
      />
    </Dialog>
  );
};

const TextEditorContainer = styled.div`
  position: relative;
  max-height: calc(100vh - 400px);
  overflow-y: auto;
  outline: 1px solid var(--neutral-3);
  border-radius: 10px;
  margin-bottom: 10px;

  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }

  ._selectTrigger_uazmk_307[data-placeholder]>span:first-child {
    color: var(--baseText);
  }

  /* Hide the unnecessary toolbar buttons */
  [title='Underline'] {
    display: none;
  }
  [title='Italic'] {
    border-top-right-radius: var(--radius-base);
    border-bottom-right-radius: var(--radius-base);
  }
  [aria-label='Check list'] {
    display: none;
  }
  [aria-label='Numbered list'] {
    border-top-right-radius: var(--radius-base);
    border-bottom-right-radius: var(--radius-base);
  }
`;
